import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'Douleutaras/redux/store';
import { LANGUAGE_CODES } from 'Douleutaras/constants/system';
import { SITE_COUNTRY_CODE } from 'Douleutaras/constants/localization';

export interface FooterLink {
  column: string;
  screen_name: string;
  url: string;
}

export interface FooterLinksState {
  links?: Array<FooterLink>;
}

export const getLanguageFile = async (lang = 'en', countryCode) => import(/* webpackChunkName: "i18n." */ `Douleutaras/data/footerLinks.${lang}.${countryCode}.json`);

// footerLinks.*.json are static, build-time assets — content can't change without a
// redeploy, so once resolved for a language/country pair it's cached for the lifetime
// of the process (server) or tab (client) rather than re-imported on every dispatch.
const footerLinksCache = new Map<string, FooterLinksState>();

export const fetchFooterLinks = createAsyncThunk(
  'footer/getFooterLinks',
  async ({ language, countryCode }: { language: LANGUAGE_CODES, countryCode: SITE_COUNTRY_CODE}) => {
    const cacheKey = `${language}_${countryCode}`;
    const cached = footerLinksCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const links = await getLanguageFile(language, countryCode);

    // convert to plain object
    const data = { ...links };
    footerLinksCache.set(cacheKey, data);

    return data;
  }
);

export const footerLinksSlice = createSlice({
  name: 'footerLinks',
  initialState: {} as FooterLinksState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchFooterLinks.fulfilled, (state, action) => {
      return action.payload;
    });
  }
});

export default footerLinksSlice.reducer;
export const footerLinksSelector = (state: RootState) => state.footerLinks;
